import React, { useState } from "react";
import { BUSINESS_INFO } from "../config";
import { getOptimizedImageUrl } from "../lib/utils";

export function ContactForm({
  title = "Send en mailhenvendelse til Trine",
  subtitle = "Udfyld formularen herunder for at sende en henvendelse direkte til Trines mail. Vi vender tilbage hurtigst muligt.",
  showWatermark = false,
  className = "",
  compact = false,
}) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Mandatory Honeypot for bot protection (Rule 12)
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const [fieldErrors, setFieldErrors] = useState({});

  function validate() {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Angiv venligst dit fulde navn";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Angiv venligst dit telefonnummer";
    } else if (!/^[0-9+\s()-]{8,20}$/.test(formData.phone.trim())) {
      errors.phone = "Indtast et gyldigt telefonnummer (mindst 8 cifre)";
    }
    if (!formData.email.trim()) {
      errors.email = "Angiv venligst din emailadresse";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Indtast en gyldig emailadresse";
    }
    if (!formData.description.trim()) {
      errors.description = "Skriv venligst en kort besked eller beskrivelse af din hund";
    }
    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setStatus({ submitting: true, submitted: false, error: null });

    try {
      // POST to required endpoint (Rule 12)
      const res = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          description: formData.description.trim(),
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (res.ok) {
        setStatus({ submitting: false, submitted: true, error: null });
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            "Der opstod en fejl ved afsendelse. Prøv venligst igen."
        );
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setStatus({
        submitting: false,
        submitted: false,
        error:
          `Beskeden kunne ikke sendes lige nu. Ring gerne direkte på ${BUSINESS_INFO.phone}.`,
      });
    }
  }

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-[#E5DCD1] bg-white p-6 sm:p-8 shadow-sm ${className}`}
    >
      {/* Subtle watermark illustration of the dog logo */}
      {showWatermark && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-12 -right-12 w-64 h-64 select-none opacity-[0.07]"
        >
          <img
            src={getOptimizedImageUrl(BUSINESS_INFO.logoUrl, { width: 300, quality: 70 })}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
      )}

      <div className="relative z-10 mb-4">
        <h3 className="font-heading text-xl sm:text-2xl font-medium tracking-tight text-black">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-1 text-xs text-neutral-600 leading-relaxed">
            {subtitle}
          </p>
        )}

        {/* Quick SMS banner / alternative inside the card */}
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#FAF6F0] border border-[#EBE3D8] px-3.5 py-2.5 text-xs text-neutral-800">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-comment-sms text-base text-[#2E6B39]" aria-hidden="true" />
            <span>Vil du hellere skrive direkte på mobilen?</span>
          </div>
          <a
            href={`sms:${BUSINESS_INFO.phoneRaw}`}
            className="inline-flex items-center gap-1.5 font-bold text-black underline underline-offset-2 hover:text-[#555]"
          >
            <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
            Send SMS ({BUSINESS_INFO.phone})
          </a>
        </div>
      </div>

      {status.submitted && (
        <div
          role="alert"
          className="relative z-10 mb-5 rounded-2xl bg-[#F0F7F1] border border-[#D0E7D4] p-4 text-[#2E6B39]"
        >
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-circle-check text-lg text-[#2E6B39]" aria-hidden="true" />
            <div>
              <p className="font-bold text-sm">Mange tak for din henvendelse!</p>
              <p className="text-xs mt-0.5">
                Vi har modtaget din besked og kontakter dig snarest muligt.
              </p>
            </div>
          </div>
        </div>
      )}

      {status.error && (
        <div
          role="alert"
          className="relative z-10 mb-5 rounded-2xl bg-[#FFF2F0] border border-[#FCD2CE] p-3.5 text-[#A82D22]"
        >
          <div className="flex items-center gap-2.5">
            <i className="fa-solid fa-triangle-exclamation text-base text-[#A82D22]" aria-hidden="true" />
            <p className="text-xs sm:text-sm font-medium">{status.error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="relative z-10 space-y-4">
        {/* Antispam Honeypot field (Rule 12: mandatory, absolutely hidden) */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor={`website-url-${showWatermark ? "hero" : "page"}`}>Website</label>
          <input
            type="text"
            id={`website-url-${showWatermark ? "hero" : "page"}`}
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            value={formData.website_url}
            onChange={handleChange}
          />
        </div>

        {/* Navn */}
        <div>
          <label
            htmlFor={`name-${showWatermark ? "hero" : "page"}`}
            className="block text-xs font-semibold text-black mb-0.5"
          >
            Navn <span className="text-black">*</span>
          </label>
          <input
            type="text"
            id={`name-${showWatermark ? "hero" : "page"}`}
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="F.eks. Mette Jensen"
            className={`w-full rounded-none border-0 border-b bg-transparent px-0 py-2 text-sm text-black placeholder-black/40 transition-colors focus:border-b-2 focus:border-black focus:outline-hidden ${
              fieldErrors.name ? "border-b-[#C53F33]" : "border-b-black/40"
            }`}
          />
          {fieldErrors.name && (
            <p className="mt-1 text-xs font-medium text-[#C53F33]">
              {fieldErrors.name}
            </p>
          )}
        </div>

        {/* Grid: Telefon & Email */}
        <div className={`grid grid-cols-1 gap-3.5 ${compact ? "" : "sm:grid-cols-2"}`}>
          {/* Telefon */}
          <div>
            <label
              htmlFor={`phone-${showWatermark ? "hero" : "page"}`}
              className="block text-xs font-semibold text-black mb-0.5"
            >
              Telefonnummer <span className="text-black">*</span>
            </label>
            <input
              type="tel"
              id={`phone-${showWatermark ? "hero" : "page"}`}
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="61 69 31 89"
              className={`w-full rounded-none border-0 border-b bg-transparent px-0 py-2 text-sm text-black placeholder-black/40 transition-colors focus:border-b-2 focus:border-black focus:outline-hidden ${
                fieldErrors.phone ? "border-b-[#C53F33]" : "border-b-black/40"
              }`}
            />
            {fieldErrors.phone && (
              <p className="mt-1 text-xs font-medium text-[#C53F33]">
                {fieldErrors.phone}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor={`email-${showWatermark ? "hero" : "page"}`}
              className="block text-xs font-semibold text-black mb-0.5"
            >
              Email <span className="text-black">*</span>
            </label>
            <input
              type="email"
              id={`email-${showWatermark ? "hero" : "page"}`}
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className={`w-full rounded-none border-0 border-b bg-transparent px-0 py-2 text-sm text-black placeholder-black/40 transition-colors focus:border-b-2 focus:border-black focus:outline-hidden ${
                fieldErrors.email ? "border-b-[#C53F33]" : "border-b-black/40"
              }`}
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs font-medium text-[#C53F33]">
                {fieldErrors.email}
              </p>
            )}
          </div>
        </div>

        {/* Beskrivelse */}
        <div>
          <label
            htmlFor={`desc-${showWatermark ? "hero" : "page"}`}
            className="block text-xs font-semibold text-black mb-0.5"
          >
            Beskrivelse / Race <span className="text-black">*</span>
          </label>
          <textarea
            id={`desc-${showWatermark ? "hero" : "page"}`}
            name="description"
            rows={compact ? 2 : 3}
            required
            value={formData.description}
            onChange={handleChange}
            placeholder="Oplys gerne hundens race, alder, pelsstand eller særlige hensyn..."
            className={`w-full rounded-none border-0 border-b bg-transparent px-0 py-2 text-sm text-black placeholder-black/40 transition-colors focus:border-b-2 focus:border-black focus:outline-hidden resize-none ${
              fieldErrors.description ? "border-b-[#C53F33]" : "border-b-black/40"
            }`}
          />
          {fieldErrors.description && (
            <p className="mt-1 text-xs font-medium text-[#C53F33]">
              {fieldErrors.description}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={status.submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-bold text-black shadow-xs transition-colors hover:bg-[#c9a7a0] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status.submitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin text-black" aria-hidden="true" />
              <span>Sender din mailhenvendelse...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane text-black" aria-hidden="true" />
              <span>Send mailhenvendelse</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
